import {
    CTResponseData,
    processResponse,
    RestClient,
} from '@superbet-group/web.lib.rest';
import {
    subtractDays,
    toUtcString,
} from '@superbet-group/web.lib.utilities';

import {
    mergeOfferMatches,
    OfferResponse,
} from '@core/services/offer/_private/helpers/offerResponse';
import { OfferRequestParams } from '@core/services/offer/_private/models/OfferRequestParams';
import {
    CurrentOfferStatus,
    OfferState,
} from '@core/services/offer/_private/models/enums';
import { MarketGroupDto } from '@core/mappers/struct/dtos/MarketGroupDto';
import { CategoryWithCompetitionsDto } from '@core/mappers/struct/dtos/CategoryWithCompetitionsDto';
import {
    MarketGroupPhase,
    SportDateWithActiveOffer,
} from './helpers/types';
import {
    getConfig,
    Locale,
} from '@superbet-group/web.lib.config';
import BookieDate from '@core/utils/date/BookieDate';
import { Country } from '@superbet-group/web.types';

interface OfferRequestConfig {
    params?: OfferRequestParams;
}

const EVENTS_BY_DATE_ENDPOINT = '/events/by-date';

export default class RestService {
    constructor(
        private readonly restClient: RestClient<OfferRequestConfig>,
        private readonly cmsRestClient: RestClient | null = null,
    ) { }

    public getStruct(): Promise<any> {
        return this.restClient.get('/struct');
    }

    public getPrematchEventsForTournaments(tournamentIds: number[]): Promise<any> {
        const tournamentIdList = tournamentIds.sort((a, b) => a - b).join(',');
        return processResponse(this.restClient.get(EVENTS_BY_DATE_ENDPOINT, {
            params: {
                tournamentIds: tournamentIdList,
                startDate: toUtcString(BookieDate.getStartDateForDate(new Date())),
            },
        }));
    }

    public getLiveEventsForTournaments(tournamentIds: number[]): Promise<any> {
        const tournamentIdList = tournamentIds.sort((a, b) => a - b).join(',');
        return processResponse(this.restClient.get(EVENTS_BY_DATE_ENDPOINT, {
            params: {
                currentStatus: CurrentOfferStatus.active,
                offerState: OfferState.live,
                tournamentIds: tournamentIdList,
                // To prevent live events with a start time in the past from flickering with SSE updates,
                // we look for events from the last 7 days, instead from the current day
                startDate: toUtcString(subtractDays(7, BookieDate.getStartDateForDate(new Date()))),
            },
        }));
    }

    public getEvents(startDate: Date, endDate: Date, sportId?: number): Promise<any> {
        return processResponse(this.restClient.get(EVENTS_BY_DATE_ENDPOINT,
            {
                params: {
                    offerState: OfferState.prematch,
                    startDate: toUtcString(startDate),
                    endDate: toUtcString(endDate),
                    sportId,
                },
            },
        ));
    }

    public getEventByBetRadarId(eventId: number): Promise<any> {
        return processResponse(this.restClient.get(`/events/betradar/${eventId}`));
    }

    public getLiveEvents(): Promise<any> {
        return processResponse(this.restClient.get(EVENTS_BY_DATE_ENDPOINT,
            {
                params: {
                    currentStatus: CurrentOfferStatus.active,
                    offerState: OfferState.live,
                    // To prevent live events with a start time in the past from flickering with SSE updates,
                    // we look for events from the last 7 days, instead from the current day
                    startDate: toUtcString(subtractDays(7, BookieDate.getStartDateForDate(new Date()))),
                },
            },
        ));
    }

    public getSuperEvents(startDate: Date): Promise<any> {
        return processResponse(this.restClient.get('/events/promoted',
            {
                params: {
                    startDate: toUtcString(startDate), // to be removed - params are not needed for v2
                },
            },
        ));
    }

    public getTopEventIds(): Promise<any> {
        return processResponse(this.restClient.get('/events/top-list'));
    }

    getOutrights(): Promise<any> {
        return processResponse(this.restClient.get('/events/outrights/all'));
    }

    getSuperAdvantageEvents(startDate: Date, endDate: Date): Promise<any> {
        const params = {
            startDate: toUtcString(BookieDate.getStartDateForDate(startDate)),
            endDate: toUtcString(BookieDate.getEndDateForDate(endDate)),
            superAdvantage: true,
        };
        return processResponse(this.restClient.get(EVENTS_BY_DATE_ENDPOINT, { params }));
    }

    getPriceBoostEvents(startDate: Date, endDate: Date): Promise<any> {
        const params = {
            startDate: toUtcString(BookieDate.getStartDateForDate(startDate)),
            endDate: toUtcString(BookieDate.getEndDateForDate(endDate)),
        };
        return processResponse(this.restClient.get('/events/boosted/by-date', { params }));
    }

    public getEventById(eventId: number, params?: OfferRequestParams): Promise<any> {
        return this.restClient.get(`/events/${eventId}`, { params });
    }

    public async getEventsById(eventIds: number[], params?: OfferRequestParams): Promise<OfferResponse> {
        const promises = eventIds.map((eventId) => this.getEventById(eventId, params));
        const results = await Promise.all(promises);
        return mergeOfferMatches(results);
    }

    public getMarketGroups(sportId: number, phase: MarketGroupPhase): Promise<OfferResponse<MarketGroupDto[]>> {
        return this.restClient.get(`/sport/${sportId}/phase/${phase}/market-groups`);
    }

    public getCategoryWithCompetitionsBySportId(sportId: number): Promise<OfferResponse<CategoryWithCompetitionsDto[]>> {
        return this.restClient.get(`sport/${sportId}/tournaments`);
    }

    public async getDatesWithActiveOffer(sportId: number): Promise<SportDateWithActiveOffer[]> {
        const response = await processResponse<CTResponseData<SportDateWithActiveOffer[]>>(this.restClient.get(`v2/active-offer-per-dates?sportId=${sportId}`));
        return response.data!;
    }

    public async fetchSportsWithEvents(): Promise<number[]> {
        // Move to CMSOfferService
        if (!this.cmsRestClient) {
            throw new Error('CMS rest client is not provided');
        }
        const data = await this.cmsRestClient.get<{ id: number }[]>(`/sports-offer/sports/${getConfig().countrySpecific.country === Country.Com ? Locale.EnglishBelgian : getConfig().appLocale.locale}`);
        return data.map((sport) => sport.id);
    }
}
